import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import type { AuditKeyPin } from "./use-integrity.ts";
import {
  keyStateStatus,
  keyStateLabel,
  keyPinKindLabel,
  thumbprintShort,
} from "./vocab.ts";

/**
 * Pinned vs observed audit keys for a chain (task 11.9; design 12.2 "pinned vs observed keys";
 * 12.4 "pinned-vs-observed keys clearly distinguished (icon+text, not colour-only)").
 *
 * Renders a chain's `pins` as a semantic `DenseTable`. The pinned/observed distinction is carried
 * THREE non-colour ways on every row: a `StatusBadge` (glyph + visible level word), an explicit
 * "Pinned key" / "Observed key" kind label, and the state word itself — so it is legible without
 * relying on colour (12.4). A `pinned` key reads healthy; an `observed_unpinned` (trust-on-first-use
 * / observed, not yet approved) key is a warn — never green, because it is not yet a pinned trust
 * anchor; a `retired` key is grey. The key id, thumbprint and first-seen time are shown so an
 * operator can audit exactly which key is trusted.
 */

export interface KeyPinsProps {
  readonly pins: readonly AuditKeyPin[];
}

export function KeyPins(props: KeyPinsProps): ReactNode {
  const columns: readonly Column<AuditKeyPin>[] = [
    {
      key: "kind",
      header: "Key",
      isRowHeader: true,
      cell: (pin) => (
        // The pinned-vs-observed distinction, by icon+text (never colour alone): the kind word
        // plus the status badge both say which it is.
        <span className="itg-keypin">
          <StatusBadge
            level={keyStateStatus(pin.state)}
            label={keyStateLabel(pin.state)}
            title={`${keyPinKindLabel(pin)}: key ${pin.kid} is ${keyStateLabel(
              pin.state,
            ).toLowerCase()}.`}
            size="sm"
          />
          <span className="itg-keypin__kind">{keyPinKindLabel(pin)}</span>
        </span>
      ),
    },
    {
      key: "kid",
      header: "Key id",
      cell: (pin) => <code className="itg-code">{pin.kid}</code>,
    },
    {
      key: "thumbprint",
      header: "Thumbprint",
      cell: (pin) => (
        <code className="itg-code" title={pin.jwk_thumbprint}>
          {thumbprintShort(pin.jwk_thumbprint)}
        </code>
      ),
    },
    {
      key: "first_seen",
      header: "First seen",
      align: "right",
      cell: (pin) => (
        <time
          className="itg-num"
          dateTime={isoAttr(pin.first_seen)}
          title={isoAttr(pin.first_seen)}
        >
          {formatDateTime(pin.first_seen)}
        </time>
      ),
    },
  ];

  return (
    <DenseTable
      caption="Audit keys for this chain: pinned vs observed"
      columns={columns}
      rows={props.pins}
      rowKey={(pin) => pin.id}
      emptyMessage="No audit keys have been seen for this chain yet."
    />
  );
}
